import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { CommunicationTemplateService } from '@core/services/communication-template.service';
import { ApiResponse } from '@core/types';
import {
  CommunicationTemplate,
  CommunicationTemplateCreate,
  CommunicationTemplateUpdate,
  CommunicationTemplatePreviewRequest,
  CommunicationTemplateType
} from '@core/types/communication-template.type';
import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CommunicationTemplateQuery {
  private readonly service = inject(CommunicationTemplateService);
  private readonly queryClient = inject(QueryClient);

  useTemplates(type?: CommunicationTemplateType) {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.communicationTemplates.list(type),
      queryFn: () => lastValueFrom(this.service.getTemplates(type)),
      select: (data: ApiResponse<CommunicationTemplate[]>) => data.data ?? [],
      staleTime: environment.staleTime,
    }));
  }

  useCreateTemplate() {
    return injectMutation(() => ({
      mutationFn: (data: CommunicationTemplateCreate) =>
        lastValueFrom(this.service.createTemplate(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.communicationTemplates.all });
      },
    }));
  }

  useUpdateTemplate() {
    return injectMutation(() => ({
      mutationFn: (data: CommunicationTemplateUpdate) =>
        lastValueFrom(this.service.updateTemplate(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.communicationTemplates.all });
      },
    }));
  }

  useDeleteTemplate() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteTemplate(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.communicationTemplates.all });
      },
    }));
  }

  useSetDefault() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.setDefault(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.communicationTemplates.all });
      },
    }));
  }

  usePreview() {
    return injectMutation(() => ({
      mutationFn: (request: CommunicationTemplatePreviewRequest) =>
        lastValueFrom(this.service.preview(request)),
    }));
  }
}
